import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="page-content" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
      <button 
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'none',
          border: 'none',
          color: '#9D4EDD',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '32px',
          padding: '8px 0'
        }}
      >
        <ArrowLeft size={20} />
        Back
      </button>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <FileText size={32} color="white" />
        </div>
        <div>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            Terms & Conditions
          </h1>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Last updated: January 2025
          </p>
        </div>
      </div>

      <div style={{
        background: 'rgba(157, 78, 221, 0.1)',
        border: '2px solid rgba(157, 78, 221, 0.3)',
        borderRadius: '16px',
        padding: '40px',
        lineHeight: '1.8',
        color: '#ccc'
      }}>
        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '0' }}>
          Introduction and Acceptance of Terms
        </h2>
        <p style={{ marginBottom: '20px' }}>
          By visiting or using the MetaSoul® Music streaming platform (the "Service"), you agree to comply with these Terms & Conditions ("Terms"). Mystic Temple, Inc. ("Mystic Temple," "we," "us" or "our") is a U.S. non‑profit corporation and owns and operates the Service. If you do not agree to these Terms, please do not use the Service. We may update these Terms at any time; using the Service after changes means you accept the revised Terms. This approach, allowing us to modify terms and terminating access for users who reject them, reflects standard practice for online services. When we update the Terms, we will post the changes on the Service or notify you by other reasonable means; continued use after notification constitutes acceptance of the new Terms.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Eligibility and Account Creation
        </h2>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Age Requirement and Authority:</strong> You must be at least 13 years old (or the equivalent minimum age in your jurisdiction) to use the Service, and you must have the authority to agree to these Terms on behalf of yourself or any entity you represent. This mirrors common eligibility requirements for streaming services, which require users to be of age and capable of entering a binding contract.
        </p>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Account Accuracy:</strong> If you register an account, you must provide accurate and complete information. Impersonating another person or using another individual's account is prohibited; such conduct can lead to account suspension or termination. You are responsible for maintaining the confidentiality of your password and all activities that occur under your account.
        </p>
        <p style={{ marginBottom: '20px' }}>
          <strong style={{ color: '#fff' }}>Account Suspension:</strong> We reserve the right to suspend or terminate your account if we determine that your registration information is inaccurate, false or misleading, or if you violate these Terms. This ensures we can maintain the integrity of the Service and protect our community.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Changes to the Service
        </h2>
        <p style={{ marginBottom: '20px' }}>
          Our platform is evolving. We may change, discontinue or add functionality to the Service at any time. Such changes may include modifications to how the Service operates, temporary outages for maintenance, or new features, similar to the way Spotify and other streaming services reserve the right to modify their services. While we'll try to give notice where possible, you acknowledge that the Service may be unavailable without notice and that we are not liable for any interruption or modification.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Grant of Access and Intellectual‑Property Rights
        </h2>
        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          Ownership of Content
        </h3>
        <p style={{ marginBottom: '20px' }}>
          MetaSoul® Music streams only original songs created or otherwise owned by Mystic Temple. We hold the copyright to all music on the platform; users do not receive any ownership rights by using the Service. Our website and mobile applications contain material protected by copyright, trademark and other intellectual‑property laws. Except as expressly provided in these Terms, nothing gives you a right to use our name, trademarks, logos or other brand features.
        </p>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          Limited License for Personal Use
        </h3>
        <p style={{ marginBottom: '20px' }}>
          Mystic Temple grants you a limited, non‑exclusive, non‑transferable and revocable license to access and stream the content available on the Service for your personal, non‑commercial use. This license does not permit you to download, reproduce, distribute, modify, publicly perform or otherwise exploit any of our music, code or website content except as permitted by law. You may not decompile, reverse‑engineer or create derivative works of the Service. This limited license is comparable to the access rights granted by other platforms, where content may be streamed but not commercially exploited.
        </p>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          User Content
        </h3>
        <p style={{ marginBottom: '20px' }}>
          Although MetaSoul® currently streams only Mystic Temple music, the Service may provide interactive features such as comments, playlists or messages. "User Content" includes text, graphics, audio, video or other materials that users post, upload or otherwise make available through the Service. You are responsible for the legality, reliability and appropriateness of any User Content you submit. By posting User Content, you grant Mystic Temple a non‑exclusive, transferable, sublicensable, worldwide, royalty‑free license to use, modify, reproduce, display and distribute that content in connection with the Service. You represent that you have the necessary rights to grant this license and that your content does not violate any privacy, publicity or intellectual‑property rights.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Prohibited Conduct
        </h2>
        <p style={{ marginBottom: '16px' }}>
          We expect our community to act respectfully. You agree not to:
        </p>
        <ul style={{ marginBottom: '20px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '8px' }}>Use language or transmit content that is profane, obscene, sexually explicit or excessively violent.</li>
          <li style={{ marginBottom: '8px' }}>Harass, threaten or defame other users; this includes insults, ethnic or homophobic slurs and invasive statements.</li>
          <li style={{ marginBottom: '8px' }}>Submit content that infringes any third‑party proprietary rights or violates any law.</li>
          <li style={{ marginBottom: '8px' }}>Publish falsehoods or misrepresentations about Mystic Temple, our users or any third party.</li>
          <li style={{ marginBottom: '8px' }}>Post private information of others without permission.</li>
          <li style={{ marginBottom: '8px' }}>Upload unlawful, defamatory, hateful or discriminatory content.</li>
          <li style={{ marginBottom: '8px' }}>Post advertisements or solicit users' passwords or personal data.</li>
        </ul>
        <p style={{ marginBottom: '20px' }}>
          We reserve the right to monitor and remove content or disable accounts we believe violate these Terms or applicable law. You can remove your own content by deleting it, but we are not responsible for residual copies that may remain.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          DMCA and Copyright Policy
        </h2>
        <p style={{ marginBottom: '20px' }}>
          Mystic Temple respects intellectual‑property law and expects users to do the same. If you believe that any material on the Service infringes your copyright, please send a notice containing the information required by the Digital Millennium Copyright Act (DMCA) to our designated agent. Your notice should include: (a) a physical or electronic signature of the copyright owner or authorized agent; (b) identification of the copyrighted work claimed to be infringed; (c) identification of the allegedly infringing material and information sufficient to locate it; (d) your contact information; (e) a statement that you have a good‑faith belief that the use is unauthorized; and (f) a statement under penalty of perjury that the information in your notice is accurate and that you are authorized to act on behalf of the copyright owner. If we receive a valid DMCA notice, we may remove or disable access to the infringing material and notify the user who posted it. We reserve the right to terminate the accounts of repeat infringers.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Privacy
        </h2>
        <p style={{ marginBottom: '20px' }}>
          Your privacy is important to us. Please review our separate Privacy Policy to understand how we collect, use and disclose information. By using the Service, you consent to our collection and use of your data in accordance with our Privacy Policy.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Third‑Party Links
        </h2>
        <p style={{ marginBottom: '20px' }}>
          The Service may contain links to third‑party websites or resources. We provide these links only as a convenience and are not responsible for the content, products or services available from such sites. You assume all risk arising from using any third‑party website or resource.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Disclaimers and Limitations of Liability
        </h2>
        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          No Warranties
        </h3>
        <p style={{ marginBottom: '20px' }}>
          The Service and all content are provided "as is" without any warranty of any kind. We explicitly disclaim all warranties of merchantability, fitness for a particular purpose, quiet enjoyment, non‑infringement and any warranties arising out of course of dealing or usage of trade. We do not warrant that the Service will meet your requirements or be available without interruption or error.
        </p>

        <h3 style={{ fontSize: '20px', color: '#fff', marginBottom: '16px', marginTop: '32px' }}>
          Limitation of Liability
        </h3>
        <p style={{ marginBottom: '20px' }}>
          To the fullest extent permitted by law, Mystic Temple and its directors, officers, employees and agents will not be liable for any incidental, special, exemplary or consequential damages, including loss of profits, loss of data, service interruption or the cost of substitute services arising out of or related to your use of the Service. In no event will our total liability for all claims exceed the greater of one hundred U.S. dollars ($100) or the amount you paid (if any) for access to the Service during the twelve months preceding the claim. These limitations mirror the liability caps commonly found in terms for other music services.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Indemnification
        </h2>
        <p style={{ marginBottom: '20px' }}>
          You agree to defend, indemnify and hold harmless Mystic Temple, its directors, officers, employees and agents from any claims, damages, losses, liabilities and expenses (including reasonable attorney's fees) arising from: (a) your use of the Service; (b) your violation of these Terms; or (c) your violation of any third‑party rights, including intellectual‑property rights. This indemnity clause ensures that users bear responsibility for their own conduct and is consistent with industry practice.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Termination
        </h2>
        <p style={{ marginBottom: '20px' }}>
          We may terminate or suspend your access to the Service at our sole discretion at any time and without notice. You may cancel your account via the account settings. Upon termination, all provisions that by their nature should survive, including ownership provisions, indemnification, warranty limitations and dispute resolution, will remain in effect.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Governing Law and Dispute Resolution
        </h2>
        <p style={{ marginBottom: '20px' }}>
          These Terms constitute the entire agreement between you and Mystic Temple regarding the Service. They are governed by and construed in accordance with the laws of the State of California, U.S.A., without regard to its conflict of law principles. If a dispute arises, please contact us at legal@MetaSoulMusic.com so we can try to resolve the issue amicably. If we cannot resolve a dispute informally, both parties agree to submit the dispute to binding arbitration in California. Each party is responsible for its own filing, administrative and arbitrator fees, and judgement on the award may be entered in any court of competent jurisdiction. You waive the right to participate in class actions or class arbitrations.
        </p>

        <h2 style={{ fontSize: '24px', color: '#fff', marginBottom: '24px', marginTop: '40px' }}>
          Miscellaneous
        </h2>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and Mystic Temple and supersede any prior agreements or understandings.
        </p>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Severability:</strong> If any provision is found unenforceable, that provision will be enforced to the maximum extent permissible and the remaining provisions will remain in full effect.
        </p>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Waiver:</strong> Our failure to enforce any right or provision of these Terms is not a waiver of such right or provision unless acknowledged in writing.
        </p>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Assignment:</strong> You may not assign these Terms without our prior written consent; we may assign these Terms without restriction.
        </p>
        <p style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#fff' }}>Notices:</strong> We may provide notices by email, posting on the Service or other reasonable methods.
        </p>
        <p style={{ marginBottom: '0' }}>
          <strong style={{ color: '#fff' }}>Contact:</strong> If you have questions about these Terms, please contact legal@MetaSoulMusic.com
        </p>
      </div>
    </div>
  );
};

export default Terms;
